/*
 * Table block (story #392) — mobile transforms.
 *
 * Reads the layout class the editor set via the custom TinyMCE plugin and, for the
 * responsive layouts, prepares the markup the SCSS (_table.scss) renders:
 *
 *   nti-table--card-mobile      -> "Card on mobile (from rows)": stamp each body cell
 *                                  with data-label = its column header, so CSS can show
 *                                  "Header: value" cards (one card per row, pure CSS).
 *                                  The header is resolved from the colspan/rowspan grid,
 *                                  so merged cells map to the right column header.
 *   nti-table--card-mobile-cols -> "Card on mobile (from columns)": build one card per
 *                                  table COLUMN after the table (a cell that spans
 *                                  several columns is repeated in each of those cards);
 *                                  CSS swaps table <-> cards by breakpoint.
 *   nti-table--accordion-mobile      -> "Accordion from rows": one collapsible card
 *                                  per row (first cell = title + toggle, remaining
 *                                  cells = body) after the table; CSS swaps
 *                                  table <-> accordion by breakpoint. Built from the
 *                                  grid model so a rowspan cell never drops a row.
 *   nti-table--accordion-mobile-cols -> "Accordion from columns": same, but one card
 *                                  per COLUMN (column header = title, the column's
 *                                  cells = body; colspan cells repeat per covered
 *                                  card).
 *   nti-table--dropdown-mobile  -> "Dropdown comparison": two pill dropdowns above
 *                                  the table each pick a column; the two chosen
 *                                  columns render side by side (full-width merged
 *                                  rows keep spanning both).
 *   nti-table--tabs-mobile      -> "Sticky Tabs + Row Headers": the first column
 *                                  (row headers) stays visible; a sticky segmented
 *                                  control above the table (one tab per DATA
 *                                  column header, merged headers yielding a single
 *                                  tab) switches which data column shows beside it.
 *                                  Hiding is done with a cell class that only takes
 *                                  effect in the mobile media query, so desktop is
 *                                  untouched.
 *
 * Tabs and Dropdown comparison need at least one row-header column plus two data
 * columns; with fewer the layout class is dropped so the table falls back to the
 * default Fixed (scroll + sticky first column) mobile behaviour.
 *
 * A block may contain more than one table (an editor can paste several into one
 * XhtmlString); every table is prepared independently.
 *
 * The DOM is prepared once on load. Which representation is visible is decided purely
 * by the media query in the SCSS, so no resize handling is needed. Accordion open/close
 * is wired with a delegated click handler.
 */

// Bootstrap gives h1–h6 a `color` of their own, which beats the text colour the editor
// set on a cell — a heading inside a white-text cell rendered black on the site while
// the edit-mode preview (which doesn't load Bootstrap) showed it white. Every cell that
// carries an inline text colour, and every card item built from one, is stamped with
// this class; _table.scss lets the headings inside it inherit the cell colour instead.
const FG_CLASS = 'nti-table-fg';

class TableBlock {

    constructor() {
        this.blocks = document.querySelectorAll('.block-table');
        if (!this.blocks.length) {
            return;
        }
        this.uid = 0; // seeds unique ids for aria-controls wiring
        this.blocks.forEach(block => this.prepare(block));
    }

    // User-visible strings are rendered (localised) as data-* attributes on the block
    // by _TableBlock.cshtml; fall back to English if a block predates them.
    labels(block) {
        const d = block.dataset;
        return {
            close: d.i18nClose || 'Close table',
            expandRow: d.i18nExpandRow || 'Expand row',
            collapseRow: d.i18nCollapseRow || 'Collapse row',
            column: d.i18nColumn || 'Column',
            selectColumns: d.i18nSelectColumns || 'Select columns to compare'
        };
    }

    prepare(block) {
        const t = this.labels(block);
        block.querySelectorAll('.block-table__scroll table').forEach(table => this.prepareTable(block, table, t));
        this.setupPopup(block, t);
    }

    prepareTable(block, table, t) {
        // Runs for every layout, including plain Fixed: the headings inside a coloured
        // cell have to follow that colour in the table itself, not just in the cards.
        this.stampTextColours(table);

        if (table.classList.contains('nti-table--card-mobile')) {
            this.stampCardLabels(table);
        }

        if (table.classList.contains('nti-table--card-mobile-cols')) {
            this.buildColumnCards(table);
        }

        if (table.classList.contains('nti-table--accordion-mobile')) {
            this.buildAccordion(table, t);
        }

        if (table.classList.contains('nti-table--accordion-mobile-cols')) {
            this.buildColumnAccordion(table, t);
        }

        if (table.classList.contains('nti-table--tabs-mobile')) {
            this.buildTabs(table, t);
        }

        if (table.classList.contains('nti-table--dropdown-mobile')) {
            this.buildDropdowns(table, t);
        }
    }

    // Popup: the expand button (rendered by _TableBlock.cshtml unless the editor ticked
    // "Disable the Popup feature") opens the table(s) full-screen over a dimmed overlay.
    setupPopup(block, t) {
        const btn = block.querySelector('.block-table__expand');
        if (!btn) {
            return;
        }
        btn.addEventListener('click', () => this.openPopup(block, btn, t));
    }

    openPopup(block, trigger, t) {
        // One overlay at a time: the trigger keeps focus, so a keyboard user could
        // otherwise stack a second overlay and leave the scroll lock dangling.
        if (document.body.classList.contains('block-table--locked')) {
            return;
        }
        const tables = block.querySelectorAll('.block-table__scroll table');
        if (!tables.length) {
            return;
        }

        const overlay = document.createElement('div');
        overlay.className = 'block-table__overlay';
        overlay.innerHTML =
            '<div class="block-table__popup" role="dialog" aria-modal="true" aria-label="' + t.close + '">' +
                '<button type="button" class="block-table__close" aria-label="' + t.close + '">' +
                    '<span class="block-table__close-label">' + t.close + '</span>' +
                    '<svg class="block-table__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false">' +
                        '<line x1="6" y1="6" x2="18" y2="18"></line>' +
                        '<line x1="18" y1="6" x2="6" y2="18"></line>' +
                    '</svg>' +
                '</button>' +
                '<div class="block-table__overlay-inner block-table">' +
                    '<div class="block-table__scroll"></div>' +
                '</div>' +
            '</div>';

        // Clone each table into the overlay; drop the layout classes so the popup always
        // shows the full desktop grid (the popup is wide, and the card/accordion JS
        // transforms only run on the in-page block, not on these clones).
        const dest = overlay.querySelector('.block-table__overlay-inner .block-table__scroll');
        tables.forEach(table => {
            const clone = table.cloneNode(true);
            clone.classList.remove(
                'nti-table--desktop-cards',
                'nti-table--card-mobile', 'nti-table--card-mobile-cols',
                'nti-table--accordion-mobile', 'nti-table--accordion-mobile-cols',
                'nti-table--tabs-mobile', 'nti-table--dropdown-mobile', 'nti-table--fixed');
            dest.appendChild(clone);
        });

        document.body.appendChild(overlay);
        document.body.classList.add('block-table--locked');

        const popup = overlay.querySelector('.block-table__popup');
        const closeBtn = overlay.querySelector('.block-table__close');

        const close = () => {
            overlay.remove();
            document.body.classList.remove('block-table--locked');
            document.removeEventListener('keydown', onKey);
            if (trigger && typeof trigger.focus === 'function') {
                trigger.focus(); // restore focus to where it was
            }
        };

        // Keep Tab focus inside the dialog; Esc closes it.
        const onKey = (e) => {
            if (e.key === 'Escape') {
                close();
                return;
            }
            if (e.key !== 'Tab') {
                return;
            }
            const focusable = popup.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
            if (!focusable.length) {
                e.preventDefault();
                return;
            }
            const first = focusable[0];
            const last = focusable[focusable.length - 1];
            if (e.shiftKey && document.activeElement === first) {
                e.preventDefault();
                last.focus();
            } else if (!e.shiftKey && document.activeElement === last) {
                e.preventDefault();
                first.focus();
            }
        };

        closeBtn.addEventListener('click', close);
        // Click on the dimmed area (overlay or the padding around the table) closes it.
        overlay.addEventListener('click', (e) => {
            if (e.target === overlay || e.target === popup) { close(); }
        });
        document.addEventListener('keydown', onKey);

        closeBtn.focus(); // move focus into the dialog
    }

    // Resolve the table into a dense grid: grid[row][col] = the cell covering that
    // position. A cell with colspan/rowspan occupies every position it spans, so
    // merged cells can be read back per covered column (or row).
    buildGrid(table) {
        const grid = [];
        Array.from(table.rows).forEach((row, r) => {
            grid[r] = grid[r] || [];
            let c = 0;
            Array.from(row.cells).forEach(cell => {
                while (grid[r][c] !== undefined) {
                    c += 1; // skip positions claimed by rowspans from earlier rows
                }
                const colSpan = cell.colSpan || 1;
                const rowSpan = cell.rowSpan || 1;
                for (let i = 0; i < rowSpan; i += 1) {
                    grid[r + i] = grid[r + i] || [];
                    for (let j = 0; j < colSpan; j += 1) {
                        grid[r + i][c + j] = cell;
                    }
                }
                c += colSpan;
            });
        });
        return grid;
    }

    gridColCount(grid) {
        return grid.reduce((max, row) => Math.max(max, row.length), 0);
    }

    // Flag every cell the editor gave a text colour (see FG_CLASS). Done before the
    // popup clones the table, so the clones carry the flag too.
    stampTextColours(table) {
        table.querySelectorAll('th, td').forEach(cell => {
            if (cell.style && cell.style.color) {
                cell.classList.add(FG_CLASS);
            }
        });
    }

    // Insert JS-built markup right after (or before) a specific table, so a block with
    // several tables lays each one's cards/accordion/tabs next to its own table.
    insertAfter(table, node) {
        table.insertAdjacentElement('afterend', node);
    }

    insertBefore(table, node) {
        table.insertAdjacentElement('beforebegin', node);
    }

    // "Card on mobile (from rows)": stamp each body cell with its column header so the
    // CSS can render a "Header: value" pair. Headers are read from the grid, so a cell
    // merged across columns is labelled by its first covered column's header.
    stampCardLabels(table) {
        const grid = this.buildGrid(table);
        const headers = (grid[0] || []).map(cell => (cell ? cell.textContent.trim() : ''));
        for (let r = 1; r < grid.length; r += 1) {
            const row = grid[r] || [];
            for (let c = 0; c < row.length; c += 1) {
                const cell = row[c];
                if (!cell) {
                    continue;
                }
                if (c > 0 && row[c - 1] === cell) {
                    continue; // colspan continuation — already labelled at its first column
                }
                const label = headers[c];
                if (label && !cell.hasAttribute('data-label')) {
                    cell.setAttribute('data-label', label);
                }
            }
        }
    }

    // "Card on mobile (from columns)": one card per table column, stacking that
    // column's cells top to bottom (the column header becomes the card title). A cell
    // merged across several columns belongs to each of them, so it is repeated in
    // every covered card; a cell merged across rows appears once per card.
    buildColumnCards(table) {
        if (table.__ntiColcards) {
            return; // already built for this table
        }
        table.__ntiColcards = true;

        const grid = this.buildGrid(table);
        const colCount = this.gridColCount(grid);
        if (!colCount) {
            return;
        }

        const cards = document.createElement('div');
        cards.className = 'block-table__colcards';

        for (let c = 0; c < colCount; c += 1) {
            const card = document.createElement('div');
            card.className = 'block-table__colcard';

            for (let r = 0; r < grid.length; r += 1) {
                const cell = grid[r][c];
                if (!cell) {
                    continue;
                }
                if (r > 0 && grid[r - 1] && grid[r - 1][c] === cell) {
                    continue; // rowspan continuation — already added to this card
                }

                const item = document.createElement('div');
                item.className = 'block-table__colcard-item';
                item.innerHTML = cell.innerHTML;
                // Carry the cell's inline alignment and Background/Text colours over;
                // a fill also gets the --fill class so CSS can bleed it to the card edges.
                if (cell.style) {
                    if (cell.style.textAlign) {
                        item.style.textAlign = cell.style.textAlign;
                    }
                    if (cell.style.color) {
                        item.style.color = cell.style.color;
                        item.classList.add(FG_CLASS);
                    }
                    if (cell.style.backgroundColor) {
                        item.style.backgroundColor = cell.style.backgroundColor;
                        item.classList.add('block-table__colcard-item--fill');
                    }
                }
                card.appendChild(item);
            }

            if (card.children.length) {
                cards.appendChild(card);
            }
        }

        this.insertAfter(table, cards);
    }

    // One collapsible accordion card: titleCell is the always-visible title next
    // to a round plus/cross toggle; bodyCells stack in the collapsible body with
    // dividers. All of them keep their inline Background/Text colours.
    addAccordionItem(accordion, titleCell, bodyCells, t) {
        const item = document.createElement('div');
        item.className = 'block-table__accordion-item';

        const header = document.createElement('div');
        header.className = 'block-table__accordion-header';

        const title = document.createElement('div');
        title.className = 'block-table__accordion-title';
        title.innerHTML = titleCell.innerHTML;
        // A fill on the title cell paints the whole header bar (the text colour
        // and alignment travel with the title itself).
        if (titleCell.style) {
            if (titleCell.style.textAlign) {
                title.style.textAlign = titleCell.style.textAlign;
            }
            if (titleCell.style.color) {
                header.style.color = titleCell.style.color;
                header.classList.add(FG_CLASS);
            }
            if (titleCell.style.backgroundColor) {
                header.style.backgroundColor = titleCell.style.backgroundColor;
            }
        }
        header.appendChild(title);

        item.appendChild(header);
        accordion.appendChild(item);

        // Nothing to expand (e.g. a row that is one full-width merged cell):
        // render a plain card with no toggle or body.
        if (!bodyCells.length) {
            return;
        }

        this.uid += 1;
        const bodyId = 'nti-acc-body-' + this.uid;

        const toggle = document.createElement('button');
        toggle.type = 'button';
        toggle.className = 'block-table__accordion-toggle';
        toggle.setAttribute('aria-expanded', 'false');
        toggle.setAttribute('aria-controls', bodyId);
        toggle.setAttribute('aria-label', t.expandRow);
        header.appendChild(toggle);

        const body = document.createElement('div');
        body.className = 'block-table__accordion-body';
        body.id = bodyId;
        body.setAttribute('role', 'region');

        bodyCells.forEach(cell => {
            const value = document.createElement('div');
            value.className = 'block-table__accordion-cell';
            value.innerHTML = cell.innerHTML;
            if (cell.style) {
                if (cell.style.textAlign) {
                    value.style.textAlign = cell.style.textAlign;
                }
                if (cell.style.color) {
                    value.style.color = cell.style.color;
                    value.classList.add(FG_CLASS);
                }
                if (cell.style.backgroundColor) {
                    value.style.backgroundColor = cell.style.backgroundColor;
                    value.classList.add('block-table__accordion-cell--fill');
                }
            }
            body.appendChild(value);
        });

        item.appendChild(body);

        // Smooth expand/collapse: the CSS transitions `height`, so animate
        // between 0 and the measured content height (scrollHeight includes it
        // even while the box is collapsed). After expanding, height goes to
        // auto so the open body can reflow freely.
        body.addEventListener('transitionend', event => {
            if (event.propertyName === 'height' && toggle.getAttribute('aria-expanded') === 'true') {
                body.style.height = 'auto';
            }
        });

        header.addEventListener('click', () => {
            const open = toggle.getAttribute('aria-expanded') === 'true';
            toggle.setAttribute('aria-expanded', open ? 'false' : 'true');
            toggle.setAttribute('aria-label', open ? t.expandRow : t.collapseRow);
            body.classList.toggle('is-open', !open);
            body.style.height = `${body.scrollHeight}px`;
            if (open) {
                body.getBoundingClientRect(); // flush the pinned height so 0 transitions
                body.style.height = '0px';
            }
        });
    }

    // "Sticky Tabs + Row Headers": the first column (the row headers) is always
    // visible; a segmented control with one tab per DATA column header (a header
    // merged across columns yields a single tab covering them) switches which data
    // column(s) show beside it. Switching marks every cell outside the visible set
    // with a class the mobile CSS hides — so desktop is untouched.
    buildTabs(table, t) {
        if (table.__ntiTabs) {
            return; // already built for this table
        }
        table.__ntiTabs = true;

        const grid = this.buildGrid(table);
        const colCount = this.gridColCount(grid);
        if (colCount < 3) {
            // A single data column — nothing to switch between. Drop the layout class
            // so the table falls back to the default Fixed (scroll + sticky first
            // column) mobile behaviour instead of rendering as a bare grid.
            table.classList.remove('nti-table--tabs-mobile');
            return;
        }

        const cellRange = this.cellColumnRanges(grid);

        // One tab per unique data-column header cell (skip a header cell repeated across
        // the columns it spans, so a merged header yields a single tab — not duplicates).
        const headerRow = grid[0] || [];
        const tabsData = [];
        for (let c = 1; c < colCount; c += 1) {
            const cell = headerRow[c];
            if (!cell) {
                continue;
            }
            if (c > 1 && headerRow[c - 1] === cell) {
                continue; // colspan continuation of the previous header
            }
            const range = cellRange.get(cell) || [c, c];
            tabsData.push({ label: cell.textContent.trim() || (t.column + ' ' + (c + 1)), range: range });
        }

        const tabs = document.createElement('div');
        tabs.className = 'block-table__tabs';
        tabs.setAttribute('role', 'tablist');
        tabs.setAttribute('aria-label', t.selectColumns);

        const buttons = [];

        const setActive = (idx) => {
            const active = tabsData[idx].range;
            cellRange.forEach((range, cell) => {
                const visible = range[0] === 0 || (range[0] <= active[1] && range[1] >= active[0]);
                cell.classList.toggle('block-table__tab-hidden', !visible);
            });
            buttons.forEach((btn, i) => {
                const isActive = i === idx;
                btn.classList.toggle('is-active', isActive);
                btn.setAttribute('aria-selected', isActive ? 'true' : 'false');
                btn.setAttribute('tabindex', isActive ? '0' : '-1'); // roving tabindex
            });
        };

        tabsData.forEach((data, idx) => {
            const tab = document.createElement('button');
            tab.type = 'button';
            tab.className = 'block-table__tab';
            tab.setAttribute('role', 'tab');
            tab.setAttribute('tabindex', '-1');
            tab.textContent = data.label;
            tab.addEventListener('click', () => setActive(idx));
            tab.addEventListener('keydown', (e) => {
                let next = -1;
                if (e.key === 'ArrowRight' || e.key === 'ArrowDown') { next = (idx + 1) % tabsData.length; }
                else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') { next = (idx - 1 + tabsData.length) % tabsData.length; }
                else if (e.key === 'Home') { next = 0; }
                else if (e.key === 'End') { next = tabsData.length - 1; }
                if (next >= 0) {
                    e.preventDefault();
                    setActive(next);
                    buttons[next].focus();
                }
            });
            buttons.push(tab);
            tabs.appendChild(tab);
        });

        this.insertBefore(table, tabs);
        setActive(0); // initial view: row headers + the first data column
    }

    // "Dropdown comparison": two pill dropdowns above the table each pick a
    // column (options = every column header); the two chosen columns render side
    // by side, and cells merged across all columns (section label rows) keep
    // spanning both. Picking the same column in both keeps the other on its
    // previous choice, so any two columns can be compared.
    buildDropdowns(table, t) {
        if (table.__ntiDropdowns) {
            return; // already built for this table
        }
        table.__ntiDropdowns = true;

        const grid = this.buildGrid(table);
        const colCount = this.gridColCount(grid);
        if (colCount < 3) {
            // Two columns are always both visible — nothing to pick. Fall back to the
            // default Fixed mobile behaviour rather than a bare grid.
            table.classList.remove('nti-table--dropdown-mobile');
            return;
        }

        const cellRange = this.cellColumnRanges(grid);

        const labels = [];
        for (let c = 0; c < colCount; c += 1) {
            const headerCell = grid[0] ? grid[0][c] : null;
            labels.push(headerCell ? headerCell.textContent.trim() : (t.column + ' ' + (c + 1)));
        }

        const selects = document.createElement('div');
        selects.className = 'block-table__selects';

        const makeSelect = (ariaSuffix) => {
            const select = document.createElement('select');
            select.className = 'block-table__select';
            select.setAttribute('aria-label', t.selectColumns + ' (' + ariaSuffix + ')');
            labels.forEach((text, c) => {
                const option = document.createElement('option');
                option.value = c;
                option.textContent = text;
                select.appendChild(option);
            });
            selects.appendChild(select);
            return select;
        };
        const left = makeSelect('1');
        const right = makeSelect('2');

        // The chevron is a background-image SVG, so its colour can't be `currentColor`;
        // rebuild it in the pill's text colour when the header sets a custom one.
        const chevron = (stroke) =>
            "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath d='M1 1l5 5 5-5' fill='none' stroke='" +
            encodeURIComponent(stroke) + "' stroke-width='2'/%3E%3C/svg%3E\")";

        // Carry the column header's fill and text colour onto its pill, so a green/white
        // header reads as a green/white pill (the header row itself is hidden on mobile).
        const paintPill = (select, col) => {
            const headerCell = grid[0] ? grid[0][col] : null;
            const bg = headerCell && headerCell.style ? headerCell.style.backgroundColor : '';
            const fg = headerCell && headerCell.style ? headerCell.style.color : '';
            select.style.backgroundColor = bg || '';
            select.style.color = fg || '';
            select.style.borderColor = bg || ''; // a fill makes it a solid chip; else CSS default
            select.style.backgroundImage = fg ? chevron(fg) : '';
        };

        let current = [0, 1];

        const apply = () => {
            left.value = String(current[0]);
            right.value = String(current[1]);
            paintPill(left, current[0]);
            paintPill(right, current[1]);
            cellRange.forEach((range, cell) => {
                const visible = current.some(c => c >= range[0] && c <= range[1]);
                cell.classList.toggle('block-table__tab-hidden', !visible);
            });
        };

        const onChange = (changedSide) => {
            let l = Number(left.value);
            let r = Number(right.value);
            if (l === r) {
                // comparing a column with itself is pointless — keep the OTHER side on
                // its previous choice so the user's new pick is honoured (no revert).
                if (changedSide === 0) { r = current[0]; } else { l = current[1]; }
            }
            current = [l, r];
            apply();
        };
        left.addEventListener('change', () => onChange(0));
        right.addEventListener('change', () => onChange(1));

        this.insertBefore(table, selects);
        apply(); // initial view: the first two columns
    }

    // Each cell's covered column range [firstCol, lastCol] (merged cells span several).
    cellColumnRanges(grid) {
        const cellRange = new Map();
        grid.forEach(row => {
            row.forEach((cell, c) => {
                if (!cell) {
                    return;
                }
                if (cellRange.has(cell)) {
                    cellRange.get(cell)[1] = c;
                } else {
                    cellRange.set(cell, [c, c]);
                }
            });
        });
        return cellRange;
    }

    // "Accordion from rows": every row becomes a collapsible card — title = the row's
    // first cell, body = the remaining cells. Built from the grid model, so a cell
    // that spans rows (rowspan) still contributes a title/body to each row it covers
    // instead of dropping the continuation row entirely.
    buildAccordion(table, t) {
        if (table.__ntiAccordion) {
            return; // already built for this table
        }
        table.__ntiAccordion = true;

        const grid = this.buildGrid(table);
        const colCount = this.gridColCount(grid);
        if (!grid.length) {
            return;
        }

        const accordion = document.createElement('div');
        accordion.className = 'block-table__accordion';

        for (let r = 0; r < grid.length; r += 1) {
            const row = grid[r] || [];
            const seen = new Set();
            const cells = [];
            for (let c = 0; c < colCount; c += 1) {
                const cell = row[c];
                if (!cell || seen.has(cell)) {
                    continue; // skip colspan repeats within the row
                }
                seen.add(cell);
                cells.push(cell);
            }
            if (cells.length) {
                this.addAccordionItem(accordion, cells[0], cells.slice(1), t);
            }
        }

        this.insertAfter(table, accordion);
    }

    // "Accordion from columns": one collapsible card per table COLUMN — title =
    // the column header (first row), body = the column's remaining cells. Merged
    // cells behave like in "Cards from columns": a cell spanning columns repeats
    // in each covered card; a cell spanning rows appears once per card.
    buildColumnAccordion(table, t) {
        if (table.__ntiAccordion) {
            return; // already built for this table
        }
        table.__ntiAccordion = true;

        const grid = this.buildGrid(table);
        const colCount = this.gridColCount(grid);
        if (!colCount) {
            return;
        }

        const accordion = document.createElement('div');
        accordion.className = 'block-table__accordion';

        for (let c = 0; c < colCount; c += 1) {
            const titleCell = grid[0] ? grid[0][c] : undefined;
            if (!titleCell) {
                continue;
            }

            const bodyCells = [];
            for (let r = 1; r < grid.length; r += 1) {
                const cell = grid[r][c];
                if (!cell) {
                    continue;
                }
                if (grid[r - 1] && grid[r - 1][c] === cell) {
                    continue; // rowspan continuation — already covered in this card
                }
                bodyCells.push(cell);
            }

            this.addAccordionItem(accordion, titleCell, bodyCells, t);
        }

        this.insertAfter(table, accordion);
    }
}

export default TableBlock;
